import { Capacitor } from "@capacitor/core";
import {
  ConsentStatus,
  ConsentType,
  FirebaseAnalytics,
} from "@capacitor-firebase/analytics";

// Never fall back to the retired directory property's measurement ID.
export const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID || "";
export const ANALYTICS_CONSENT_KEY = "observer_analytics_consent_v1";

export type AnalyticsConsent = "granted" | "denied" | null;
type Param = string | number | boolean | string[];

const SPEC_EVENTS = new Set([
  "candidate_discovered",
  "extraction_confidence",
  "approval_seconds",
  "profile_published",
  "candidate_declined",
  "nomination_submitted",
  "nomination_outcome",
  "link_candidate_created",
  "merge_confirmed",
  "merge_reversed",
  "not_same_entity_recorded",
  "impersonation_reported",
  "profile_view",
  "record_panel_view",
  "transparency_panel_view",
  "transparency_explainer_view",
  "offer_card_view",
  "source_click",
  "booking_link_click",
  "phone_click",
  "enquiry_submitted",
  "review_panel_view",
  "response_view",
  "shortlist_add",
  "compare_start",
  "correction_started",
  "correction_submitted",
  "claim_started",
  "claim_submitted",
  "report_submitted",
  "transparency_observation_recorded",
  "visibility_diff_computed",
  "practitioner_transparency_response_posted",
  "review_started",
  "review_submitted",
  "evidence_uploaded",
  "evidence_verified",
  "evidence_rejected",
  "review_published",
  "review_removed",
  "response_posted",
  "dispute_opened",
  "dispute_resolved",
  "monitoring_request_submitted",
  "monitoring_activated",
  "observation_confirmed_post_request",
  "dispute_triaged",
  "correction_resolved",
  "takedown_resolved",
  "throttle_engaged",
  "comparison_view",
  "share_link_created",
  "share_link_visit",
  "share_link_review_start",
  "share_link_review_submit",
  "referral_verified",
  "review_invitation_created",
  "paywall_view",
  "checkout_start",
  "purchase",
  "purchase_failed",
  "restore_started",
  "restore_completed",
  "subscription_renew",
  "subscription_cancel",
  "subscription_expire",
  "subscription_refund",
]);
const PARAM_KEYS = new Set([
  "source",
  "field",
  "approver",
  "reason",
  "signals",
  "class",
  "conflict_declared",
  "signal",
  "delta",
  "hours",
  "outcome",
  "seconds",
  "surface",
  "confidence",
  "grounds",
  "tier",
  "intent",
  "count",
  "reward_months",
  "product_id",
  "currency",
  "value",
  "store",
  "entitlement_count",
  "error_code",
  "placement",
]);
const ENUM_VALUE = /^[a-z0-9_:-]{1,64}$/;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
  return window.gtag;
}

export function getAnalyticsConsent(): AnalyticsConsent {
  try {
    const value = localStorage.getItem(ANALYTICS_CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

function loadGoogleTag() {
  if (
    !GA_MEASUREMENT_ID ||
    Capacitor.isNativePlatform() ||
    document.querySelector(`script[data-observer-ga="${GA_MEASUREMENT_ID}"]`)
  )
    return;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.dataset.observerGa = GA_MEASUREMENT_ID;
  document.head.appendChild(script);
  const gtag = ensureGtag();
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

async function setNativeConsent(consent: AnalyticsConsent) {
  if (!Capacitor.isNativePlatform()) return;
  const analytics =
    consent === "granted" ? ConsentStatus.Granted : ConsentStatus.Denied;
  await Promise.all([
    FirebaseAnalytics.setEnabled({ enabled: consent === "granted" }),
    FirebaseAnalytics.setConsent({
      type: ConsentType.AnalyticsStorage,
      status: analytics,
    }),
    FirebaseAnalytics.setConsent({
      type: ConsentType.AdStorage,
      status: ConsentStatus.Denied,
    }),
    FirebaseAnalytics.setConsent({
      type: ConsentType.AdUserData,
      status: ConsentStatus.Denied,
    }),
    FirebaseAnalytics.setConsent({
      type: ConsentType.AdPersonalization,
      status: ConsentStatus.Denied,
    }),
    FirebaseAnalytics.setConsent({
      type: ConsentType.PersonalizationStorage,
      status: ConsentStatus.Denied,
    }),
    FirebaseAnalytics.setUserId({ userId: null }),
  ]).catch(() => undefined);
}

export function initializeAnalytics() {
  const gtag = ensureGtag();
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    personalization_storage: "denied",
    wait_for_update: 500,
  });
  gtag("set", "ads_data_redaction", true);
  const consent = getAnalyticsConsent();
  void setNativeConsent(consent);
  if (consent === "granted" && !Capacitor.isNativePlatform()) {
    gtag("consent", "update", { analytics_storage: "granted" });
    loadGoogleTag();
  }
}

export function setAnalyticsConsent(consent: Exclude<AnalyticsConsent, null>) {
  try {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, consent);
  } catch {
    /* remains denied */
  }
  ensureGtag()("consent", "update", {
    analytics_storage: consent,
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    personalization_storage: "denied",
  });
  void setNativeConsent(consent);
  if (consent === "granted") loadGoogleTag();
}

function normalizedPath(pathname: string): string {
  if (/^\/profile\/[^/]+/.test(pathname)) return "/profile/:id";
  if (/^\/claim\/[^/]+/.test(pathname)) return "/claim/:id";
  return pathname || "/";
}

export function trackPageView(pathname: string, title = document.title) {
  if (getAnalyticsConsent() !== "granted") return;
  const path = normalizedPath(pathname);
  if (Capacitor.isNativePlatform()) {
    void FirebaseAnalytics.setCurrentScreen({
      screenName: path,
      screenClassOverride: "CapacitorWebView",
    }).catch(() => undefined);
  } else {
    ensureGtag()("event", "page_view", {
      page_title: title,
      page_location: `${window.location.origin}${path}`,
      page_path: path,
      surface: "web",
    });
  }
}

function safeParameters(parameters: Record<string, Param>) {
  const clean: Record<string, Param> = {
    surface: Capacitor.isNativePlatform() ? "native" : "web",
  };
  Object.entries(parameters).forEach(([key, value]) => {
    if (!PARAM_KEYS.has(key)) return;
    if (typeof value === "string") {
      const normalized = value.toLowerCase().trim();
      if (ENUM_VALUE.test(normalized)) clean[key] = normalized;
    } else if (typeof value === "number" || typeof value === "boolean")
      clean[key] = value;
    else if (Array.isArray(value))
      clean[key] = value
        .map((x) => x.toLowerCase().trim())
        .filter((x) => ENUM_VALUE.test(x))
        .slice(0, 10);
  });
  return clean;
}

export function trackAnalyticsEvent(
  name: string,
  parameters: Record<string, Param> = {},
) {
  if (getAnalyticsConsent() !== "granted" || !SPEC_EVENTS.has(name)) return;
  const params = safeParameters(parameters);
  if (Capacitor.isNativePlatform())
    void FirebaseAnalytics.logEvent({ name, params }).catch(() => undefined);
  else ensureGtag()("event", name, params);
}

// Identity is set only after the authenticated practitioner completes claim
// verification. Seeker analytics always retain a null user ID.
export function identifyVerifiedPractitioner(firebaseUID: string) {
  if (
    !Capacitor.isNativePlatform() ||
    getAnalyticsConsent() !== "granted" ||
    !firebaseUID
  )
    return;
  void FirebaseAnalytics.setUserId({ userId: firebaseUID }).catch(
    () => undefined,
  );
}

export function clearAnalyticsIdentity() {
  if (Capacitor.isNativePlatform())
    void FirebaseAnalytics.setUserId({ userId: null }).catch(() => undefined);
}
